/**
 * 74.3 (DECISIONS 341): the planet's spin axis and the key of its generated ground, on their own so that the wind
 * (core/windField.ts) and the world sheet (core/worldSheet.ts, which reads the wind) can both read them without a cycle.
 * 74.12 step 0 (DECISIONS 368): the wind's bands blow about this axis, the one the sky turns about. Pure: dmath.
 */
import type { PlanetState, Vec3 } from '../types';
import { dsin, dcos } from './dmath';
import { SeededRandom } from './random';
import { vecAdd, vecCross, vecNormalize, vecScale } from '../math';
import { typedArrayHash } from './contentHash';

/** A key for the planet's generated ground (its id and a hash of its generated heights): what the sheet's seeded choices hang on.
 * 74.13 step 3: a planet that took the landform pass keeps its base field's key (`baseGroundKey`), so the axis stays put (374 (6)). */
export function groundKey(planet: Pick<PlanetState, 'id' | 'heightmap' | 'initialHeightmap' | 'baseGroundKey' | 'carvedGroundKey'>): string {
  if (planet.baseGroundKey !== undefined) return planet.baseGroundKey;
  if (planet.carvedGroundKey !== undefined) return planet.carvedGroundKey;   // (74.19: a carved world without landforms)
  const h = planet.initialHeightmap ?? planet.heightmap;
  let x = 2166136261;
  const step = Math.max(1, Math.floor(h.length / 64));
  for (let i = 0; i < h.length; i += step) { x ^= Math.round(h[i] * 1e6) | 0; x = Math.imul(x, 16777619) >>> 0; }
  return `${planet.id}-${h.length}-${x}`;
}

/**
 * The key a memo of what the ground grows (the climate, the fronts, the cells) hangs on: the ground key and, on a carved
 * world, a hash of the carved generated heights. 74.19: the carve keeps the planned ground's key (`baseGroundKey`, or
 * `carvedGroundKey`) so every seeded choice is the dial off's — but the climate reads the carved ground, so a memo keyed on
 * the ground key alone gave a light world the off world's fronts when both were played in one process (the Story's
 * story-7: light after off parted from light alone at 1,320 s, weather segment 11). Uncarved: the ground key, unchanged.
 */
export function memoGroundKey(planet: Pick<PlanetState, 'id' | 'heightmap' | 'initialHeightmap' | 'baseGroundKey' | 'carvedGroundKey' | 'plannedHeightmap'>): string {
  const k = groundKey(planet);
  return planet.plannedHeightmap && planet.initialHeightmap ? `${k}|carved-${typedArrayHash(planet.initialHeightmap).full >>> 0}` : k;
}

/** The planet's spin axis (DECISIONS 341: latitude now, a seeded axis 74.2 adopts): a direction from the planet's ground key, tilted off the system's plane. */
export function spinAxisOf(planet: Pick<PlanetState, 'id' | 'heightmap' | 'initialHeightmap' | 'baseGroundKey' | 'carvedGroundKey'>): Vec3 {
  const rng = new SeededRandom(`${groundKey(planet)}-spin`);
  const tilt = rng.range(0, 0.5), az = rng.range(0, Math.PI * 2);
  return { x: dsin(tilt) * dcos(az), y: dcos(tilt), z: dsin(tilt) * dsin(az) };
}

/** A planet's frame: its spin axis and two directions across it (longitude 0 and 90°) — the wind's bands blow about it, the world sheet's lee and latitude read it, the weather's fronts ride it (74.12 step 0: one axis; moved here from windField.ts for 74.11, so the climate can read the sheet without a cycle). */
export interface PlanetFrame { axis: Vec3; e1: Vec3; e2: Vec3 }
export type AxisPlanet = Pick<PlanetState, 'id' | 'heightmap' | 'initialHeightmap'>;
/** (by the generated heights spinAxisOf keys on — the same array, so the same axis; a bare planet without heights: by id) */
const frames = new WeakMap<object, PlanetFrame>(), bareFrames = new Map<number, PlanetFrame>();
export function frameOf(planet: AxisPlanet): PlanetFrame {
  const heights: object | undefined = planet.initialHeightmap ?? planet.heightmap;
  let f = heights ? frames.get(heights) : bareFrames.get(planet.id);
  if (f) return f;
  // 74.12 step 0 (DECISIONS 368): the planet's spin axis; a bare planet (a test's, no heights) the system's up, as the sky's
  const axis = heights ? vecNormalize(spinAxisOf(planet)) : { x: 0, y: 1, z: 0 };
  const e1 = vecNormalize(vecCross(axis, Math.abs(axis.x) < 0.9 ? { x: 1, y: 0, z: 0 } : { x: 0, y: 0, z: 1 }));
  f = { axis, e1, e2: vecCross(axis, e1) };
  if (heights) frames.set(heights, f); else bareFrames.set(planet.id, f);
  return f;
}

/** The planet's wind axis — 74.12 step 0: its spin axis, the sky's (core/spinAxis.ts): the bands blow east about it (the forest's lee and the world sheet's wet mask read it too). */
export function windAxis(planet: AxisPlanet): Vec3 {
  return frameOf(planet).axis;
}

/** The place at latitude `lat`, longitude `lon` in a frame (unit). */
export function placeAt(fr: PlanetFrame, lat: number, lon: number): Vec3 {
  const c = dcos(lat);
  return vecAdd(vecAdd(vecScale(fr.e1, c * dcos(lon)), vecScale(fr.e2, c * dsin(lon))), vecScale(fr.axis, dsin(lat)));
}
